import { useState } from 'react';
import { Dayjs } from 'dayjs';
import { DatepickerV2 } from '@client/toolkit';
import { MdDateRange } from 'react-icons/md';
import { colors } from '../../styles/color-tokens';

interface YearPickerProps {
  value: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
}

/**
 * Year-only picker used to select the financial year on the dashboard and reporting pages. Styled
 * to match the dark surface theme (white text on an elevated background, borderless).
 */
export function YearPicker({ value, onChange }: YearPickerProps) {
  const [open, setOpen] = useState(false);

  return (
    <DatepickerV2
      value={value}
      views={['year']}
      openTo="year"
      format="YYYY"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onChange={onChange}
      slotProps={{
        desktopPaper: {
          sx: {
            '& .MuiYearCalendar-root': {
              scrollbarWidth: 'thin',
              scrollbarColor: '#322F2C transparent',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#322F2C', borderRadius: '9999px' },
              '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#4a4642' },
            },
          },
        },
        field: { readOnly: true },
        textField: {
          size: 'small',
          onClick: () => setOpen(true),
          InputProps: {
            startAdornment: <MdDateRange size={16} className="text-brand-primary hover:cursor-pointer" />,
            endAdornment: <></>,
            onClick: () => setOpen(true),
            className: 'gap-2 cursor-pointer',
          },
          sx: {
            '& .MuiPickersInputBase-root, & .MuiOutlinedInput-root': {
              backgroundColor: colors.background.surface.elevated,
              color: 'white',
              borderRadius: '6px',
              width: 'fit-content',
            },
            '& .MuiPickersOutlinedInput-notchedOutline, & .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '&:hover .MuiPickersOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiPickersSectionList-sectionContent, & .MuiPickersInputBase-sectionAfter, & .MuiPickersInputBase-sectionBefore, & .MuiInputBase-input':
              { color: 'white', fontSize: '14px' },
          },
        },
      }}
    />
  );
}
